import { useCallback, useEffect, useState } from "react";
import { getErrorMessage } from "../utils/errors";

export interface AsyncData<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

interface State<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
}

/**
 * Runs `load` whenever it changes (memoize it with useCallback) or `reload` is called.
 * The previous request is aborted so a slow, stale response never overwrites a newer one.
 * Previous data stays visible while reloading.
 */
export const useAsyncData = <T>(
  load: (signal: AbortSignal) => Promise<T>
): AsyncData<T> => {
  const [state, setState] = useState<State<T>>({
    data: undefined,
    loading: true,
    error: undefined,
  });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: undefined }));
    load(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) {
          setState({ data, loading: false, error: undefined });
        }
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: getErrorMessage(error),
          }));
        }
      }
    );
    return () => controller.abort();
  }, [load, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { ...state, reload };
};
